/**
 * Lottie — renders a Lottie animation at a FIXED size, using lottie-web's
 * SVG-only "light" build. Accepts either a plain animation `.json` (fetched by
 * lottie-web via `path`) or a `.lottie` dotLottie file (a zip of manifest +
 * animation JSON, which we unzip client-side and hand over as `animationData`).
 * One runtime for both formats — no second player/WASM.
 *
 * Client-only by construction: the player (and, for dotLottie, the unzip) are
 * dynamically `import()`ed inside an effect (after mount), so neither lottie-web
 * nor fflate enters the SSR/Worker bundle (each lands in its own lazy chunk, same
 * discipline as the react-pdf renderer) and there's no hydration mismatch — the
 * server renders the empty fixed-size box and the client mounts the animation into
 * it. The box reserves space up front, so the load causes no layout shift (CLS).
 * Respects reduced motion: when the user prefers reduced motion the animation
 * loads paused on its first frame instead of looping.
 *
 * Pass `src` (a path under `/public`, `.json` or `.lottie`) plus explicit
 * `width`/`height` — the reserved box is what prevents the shift.
 */
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import type { AnimationItem } from 'lottie-web';
import styles from './Lottie.module.css';

export interface LottieProps {
  /** Path to the animation (`/animations/foo.json` or `/animations/foo.lottie`). */
  readonly src: string;
  readonly width: number | string;
  readonly height: number | string;
  readonly loop?: boolean;
  /** Accessible name — the animation renders as an `img`-role region. */
  readonly ariaLabel: string;
  // `| undefined` so a CSS-module member (`string | undefined` under
  // noUncheckedIndexedAccess) forwards cleanly under exactOptionalPropertyTypes.
  readonly className?: string | undefined;
  readonly style?: CSSProperties | undefined;
}

/**
 * Fetch a `.lottie` (dotLottie) file and return its animation JSON. A dotLottie is
 * a zip of `manifest.json` + `animations/<id>.json` (+ optional assets); read the
 * manifest for the first animation's id, fall back to the first `animations/*.json`,
 * and parse it for lottie-web's `animationData`. Called only inside the mount
 * effect, so fflate stays out of the SSR/Worker bundle.
 */
async function loadDotLottieData(src: string): Promise<object> {
  const res = await fetch(src);
  if (!res.ok) throw new Error(`lottie fetch failed: ${res.status}`);
  const bytes = new Uint8Array(await res.arrayBuffer());
  const { unzipSync, strFromU8 } = await import('fflate');
  const files = unzipSync(bytes);

  let animPath: string | undefined;
  const manifestRaw = files['manifest.json'];
  if (manifestRaw) {
    try {
      const manifest = JSON.parse(strFromU8(manifestRaw)) as {
        animations?: ReadonlyArray<{ id?: string }>;
      };
      const id = manifest.animations?.[0]?.id;
      if (id) animPath = `animations/${id}.json`;
    } catch {
      // Malformed manifest — fall through to the directory scan.
    }
  }
  if (animPath === undefined || files[animPath] === undefined) {
    animPath = Object.keys(files).find((key) => /^animations\/.+\.json$/i.test(key));
  }

  const data = animPath === undefined ? undefined : files[animPath];
  if (data === undefined) throw new Error('no animation found in .lottie file');
  return JSON.parse(strFromU8(data)) as object;
}

export function Lottie({
  src,
  width,
  height,
  loop = true,
  ariaLabel,
  className,
  style,
}: LottieProps): ReactNode {
  const containerRef = useRef<HTMLDivElement>(null);
  // Drives the fade+slide-up once the animation's SVG is built (it loads a moment
  // after mount via the lazy import + fetch). Reset per `src` so a swap re-animates.
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (containerRef.current === null) return;
    setLoaded(false);
    let destroyed = false;
    let anim: AnimationItem | undefined;
    // Guard matchMedia — jsdom (unit tests) doesn't implement it.
    const prefersReduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isDotLottie = /\.lottie(\?|#|$)/i.test(src);

    async function build(): Promise<void> {
      const [mod, animationData] = await Promise.all([
        import('lottie-web/build/player/lottie_light'),
        isDotLottie ? loadDotLottieData(src) : Promise.resolve(undefined),
      ]);
      if (destroyed || containerRef.current === null) return;
      anim = mod.default.loadAnimation({
        container: containerRef.current,
        renderer: 'svg',
        // Reduced motion → render the first frame and hold (no loop, no autoplay).
        loop: prefersReduced ? false : loop,
        autoplay: !prefersReduced,
        // dotLottie is handed pre-parsed data; a plain .json is fetched by path.
        ...(isDotLottie ? { animationData } : { path: src }),
      });
      // Fade+rise the box in once the SVG DOM is ready (instead of a pop-in).
      anim.addEventListener('DOMLoaded', () => {
        if (destroyed) return;
        // Reduced motion: hold the FINAL frame (a completed check / settled art),
        // not the empty first frame a draw-in animation starts on.
        if (prefersReduced && anim) {
          anim.goToAndStop(Math.max(0, anim.totalFrames - 1), true);
        }
        setLoaded(true);
      });
    }

    void build().catch(() => {
      // A decorative animation must never break the page; on any fetch/parse
      // failure the reserved box simply stays empty.
    });

    return () => {
      destroyed = true;
      anim?.destroy();
    };
  }, [src, loop]);

  return (
    <div
      ref={containerRef}
      role="img"
      aria-label={ariaLabel}
      className={[styles.player, loaded ? styles.entered : undefined, className]
        .filter(Boolean)
        .join(' ')}
      style={{ width, height, ...style }}
    />
  );
}
